import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

// ============================================================================
// Storyblok preview token parsing and validation
// ============================================================================

export type StoryblokPreviewParams = {
  storyId: string;
  spaceId: string;
  timestamp: number;
  token: string;
};

export const parseStoryblokPreviewParams = (search: string): StoryblokPreviewParams | null => {
  const params = new URLSearchParams(search);

  const storyId = params.get('_storyblok');
  const spaceId = params.get('_storyblok_tk[space_id]');
  const timestampStr = params.get('_storyblok_tk[timestamp]');
  const token = params.get('_storyblok_tk[token]');

  if (!storyId || !spaceId || !timestampStr || !token) {
    return null;
  }

  const timestamp = parseInt(timestampStr, 10);
  if (isNaN(timestamp)) {
    return null;
  }

  return { storyId, spaceId, timestamp, token };
};

/**
 * Validates a Storyblok preview token using SHA-1 via the Web Crypto API.
 * Formula: SHA1(spaceId + ':' + previewSecret + ':' + timestamp)
 * Returns false (never throws) for expired, invalid, or uncomputable tokens.
 * Expiry window: 1 hour (3600 seconds).
 */
export const validateStoryblokToken = async (
  params: StoryblokPreviewParams,
  previewSecret: string,
): Promise<boolean> => {
  try {
    const nowSeconds = Math.floor(Date.now() / 1000);
    if (nowSeconds - params.timestamp > 3600) {
      return false;
    }

    const validationString = `${params.spaceId}:${previewSecret}:${params.timestamp}`;
    const encoded = new TextEncoder().encode(validationString);
    const hashBuffer = await crypto.subtle.digest('SHA-1', encoded);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const computedToken = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

    return computedToken.toLowerCase() === params.token.toLowerCase();
  } catch {
    return false;
  }
};

// ============================================================================
// Context
// ============================================================================

export type StoryblokPreviewState = {
  /** True while SHA-1 token validation is running asynchronously. */
  isLoading: boolean;
  /** True only when Storyblok preview params are present and the token is valid and not expired. */
  isPreviewMode: boolean;
};

const StoryblokPreviewContext = createContext<StoryblokPreviewState | undefined>(undefined);

type StoryblokPreviewProviderProps = {
  children: ReactNode;
};

export const StoryblokPreviewProvider = ({ children }: StoryblokPreviewProviderProps) => {
  const [state, setState] = useState<StoryblokPreviewState>({
    isLoading: false,
    isPreviewMode: false,
  });

  useEffect(() => {
    const rawSecret: unknown = import.meta.env['VITE_STORYBLOK_PREVIEW_SECRET'];
    const previewSecret = typeof rawSecret === 'string' && rawSecret.length > 0 ? rawSecret : null;

    if (!previewSecret) {
      return;
    }

    const params = parseStoryblokPreviewParams(window.location.search);

    if (!params) {
      return;
    }

    setState({ isLoading: true, isPreviewMode: false });

    let cancelled = false;

    validateStoryblokToken(params, previewSecret)
      .then((isValid) => {
        if (!cancelled) {
          setState({ isLoading: false, isPreviewMode: isValid });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ isLoading: false, isPreviewMode: false });
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <StoryblokPreviewContext.Provider value={state}>{children}</StoryblokPreviewContext.Provider>
  );
};

export const useStoryblokPreview = (): StoryblokPreviewState => {
  const context = useContext(StoryblokPreviewContext);
  if (context === undefined) {
    throw new Error('useStoryblokPreview must be used within a StoryblokPreviewProvider');
  }
  return context;
};

type MockStoryblokPreviewProviderProps = {
  value?: Partial<StoryblokPreviewState>;
  children: ReactNode;
};

const defaultMockState: StoryblokPreviewState = {
  isLoading: false,
  isPreviewMode: false,
};

export const MockStoryblokPreviewProvider = ({
  value,
  children,
}: MockStoryblokPreviewProviderProps) => (
  <StoryblokPreviewContext.Provider value={{ ...defaultMockState, ...value }}>
    {children}
  </StoryblokPreviewContext.Provider>
);
